
import React from "react";
import { Button } from "@/components/ui/button";
import { ChevronsUpDown } from "lucide-react";
import { useTranslations } from "use-intl";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface CurrencyFilterProps {
  currencies: string[];
  selectedCurrencies: string[] | 'all';
  onCurrencyChange: (currency: string, checked: boolean) => void;
  onSelectAllCurrencies: () => void;
}

type FilterTranslator = ReturnType<typeof useTranslations<"consultantPayables.filters">>;

function currencyTriggerLabel(selected: string[] | 'all', t: FilterTranslator): string {
  if (selected === 'all') return t("allCurrencies");
  return Array.isArray(selected) ? t("selectedCount", { count: String(selected.length) }) : t("selectCurrenciesPlaceholder");
}

const CurrencyFilter: React.FC<CurrencyFilterProps> = ({
  currencies,
  selectedCurrencies,
  onCurrencyChange,
  onSelectAllCurrencies,
}) => {
  const t = useTranslations("consultantPayables.filters");
  // Check if a currency is selected
  const isCurrencySelected = (currency: string) => {
    if (selectedCurrencies === 'all') return false;
    return Array.isArray(selectedCurrencies) && selectedCurrencies.includes(currency);
  };

  const triggerLabel = currencyTriggerLabel(selectedCurrencies, t);

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">{t("currencies")}</label>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-between"
          >
            {triggerLabel}
            <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56 max-h-[300px] overflow-auto">
          <DropdownMenuLabel>{t("selectCurrencies")}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuCheckboxItem
            checked={selectedCurrencies === 'all'}
            onCheckedChange={onSelectAllCurrencies}
          >
            {t("allCurrencies")}
          </DropdownMenuCheckboxItem>
          <DropdownMenuSeparator />
          {currencies.map((currency) => (
            <DropdownMenuCheckboxItem
              key={currency}
              checked={isCurrencySelected(currency)}
              onCheckedChange={(checked) => onCurrencyChange(currency, checked)}
            >
              {currency}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default CurrencyFilter;
